import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth, isAuthenticated, role } from "./useAuth"
import { setError, startLoading, stopLoading } from "../components/Notification"
import { getMutex } from "../utils/simpleMutex"
import { changeWarehouseAsync } from "../api"

const _isDirty = ref(0)
const _worker = ref(null)
const _isLoaded = ref(false)

const CurrentWorker = {
  _currentWorker: null,
  _mutex: getMutex(),
  _fatalError: false,
  async getCurrentWorker(fetchAuthAsync, reload) {
    const [lock, release] = this._mutex.getLock()
    await lock
    if (this._currentWorker === null || reload) {
      startLoading("getCurrentWorker")
      try {
        const raw = await fetchAuthAsync(`${AZURE_URI}/Worker/me`, "GET")
        if (raw?.status !== "ok") {
          setError(raw)
        } else if (raw?.result?.id?.length == 36) {
          this._currentWorker = raw.result
        }
      } catch (e) {
        this._fatalError = true
        setError(e)
      }
      stopLoading("getCurrentWorker")
    }
    release()
    return this._currentWorker
  },
}

let _isAuthenticatedWatcherRegistered = false

export function useWorker() {
  const { fetchAuthAsync } = useAuth()

  const loadCurrentWorkerAsync = async (reload = false) => {
    console.log("loadCurrentWorkerAsync")
    try {
      const currentWorker = await CurrentWorker.getCurrentWorker(fetchAuthAsync, reload)
      if (currentWorker?.id?.length == 36) {
        _worker.value = currentWorker
        _isDirty.value += 1
      } else {
        _worker.value = null
      }
      _isLoaded.value = true
    } catch (e) {
      setError(e)
    }
  }

  const changeActiveWarehouseAsync = async (warehouseId) => {
    if (typeof warehouseId !== "string" || warehouseId?.length !== 36) {
      console.error("warehouseId should be GUID")
      return
    }
    if (!_worker.value || _worker.value?.warehouses.length <= 0) {
      console.error("no warehouses")
      return
    }
    const warehouse = _worker.value.warehouses.find((w) => w.id === warehouseId)
    if (!warehouse) {
      console.error("no warehouse by ID: " + warehouseId)
      return
    }
    const ok = await changeWarehouseAsync(fetchAuthAsync, warehouseId)
    if (ok) {
      _worker.value.currentVehicleId = warehouseId
      _isDirty.value += 1
    }
  }

  if (!_isAuthenticatedWatcherRegistered) {
    _isAuthenticatedWatcherRegistered = true
    watch(
      isAuthenticated,
      async (newVal, oldVal) => {
        if (oldVal !== undefined && newVal === oldVal) return
        if (role.value === "Customer") return
        console.log("isAuthenticated changed in useWorker", newVal, oldVal)
        if (newVal) {
          await loadCurrentWorkerAsync()
        }
      },
      { immediate: true },
    )
  }

  return {
    worker: {
      isLoaded: computed(() => _isLoaded.value),
      id: computed(() => _worker.value?.id),
      fullName: computed(() => _worker.value?.fullName),
      workPauseStartDateTime: computed(() => _worker.value?.workPauseStartDateTime),
      activeWarehouse: computed(() => _worker.value?.warehouses?.find((w) => w.id === _worker.value?.currentVehicleId)),
      warehouses: computed(() => _worker.value?.warehouses),
      dirtySignal: computed(() => _isDirty.value),
      canSignLeakageForm: computed(() => _worker.value?.canSignLeakageForm),
    },
    changeActiveWarehouseAsync,
    loadCurrentWorkerAsync,
  }
}
